// CategoryBar Component - Airbnb-Style mit Realm-Support (GoodLife/GoodWork)

import React, { useRef, useState, useEffect } from 'react';
import {
  Globe,
  Palette,
  Sofa,
  Moon,
  Clock,
  Trees,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
  // GoodWork Icons
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
} from 'lucide-react';
import {
  MAIN_CATEGORIES,
  GOODWORK_CATEGORIES,
  MainCategory,
  GoodWorkCategory,
} from '../../types';
import { useRealm } from '../../context/RealmContext';

// Icon mapping for all categories (both realms)
const ICON_MAP: Record<string, LucideIcon> = {
  // GoodLife
  Globe,
  Palette,
  Sofa,
  Moon,
  Clock,
  Trees,
  // GoodWork
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
};

// Kurze Labels fuer Mobile - GoodLife
const SHORT_LABELS_GOODLIFE: Record<string, string> = {
  urban_creative: 'Kreativ',
  public_living_room: 'Treffpunkt',
  silent_sanctuary: 'Rueckzug',
  analoge_refugien: 'Analog',
  natur_outdoor: 'Natur',
};

// Kurze Labels fuer Mobile - GoodWork
const SHORT_LABELS_GOODWORK: Record<string, string> = {
  coworking: 'Coworking',
  focused_work: 'Fokus',
  creative_studio: 'Kreativ',
  meeting_space: 'Meeting',
  maker_space: 'Maker',
  learning_hub: 'Lernen',
};

interface CategoryBarProps {
  activeCategory: MainCategory | GoodWorkCategory | '';
  onCategoryChange: (category: MainCategory | GoodWorkCategory | '') => void;
}

export function CategoryBar({
  activeCategory,
  onCategoryChange,
}: CategoryBarProps) {
  const { realm, isGoodWork } = useRealm();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Waehle Kategorien basierend auf Realm
  const categories = isGoodWork ? GOODWORK_CATEGORIES : MAIN_CATEGORIES;
  const shortLabels = isGoodWork ? SHORT_LABELS_GOODWORK : SHORT_LABELS_GOODLIFE;
  const accentColor = isGoodWork ? 'indigo' : 'gray';

  // Check scroll position
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Left scroll button */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-md hover:shadow-lg transition-shadow"
              aria-label="Nach links scrollen"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
          )}

          {/* Categories container */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-4 sm:gap-8 lg:gap-10 overflow-x-auto no-scrollbar py-3 px-1"
          >
            {/* Alle Button */}
            <button
              onClick={() => onCategoryChange('')}
              className={`flex flex-col items-center gap-1.5 min-w-[48px] sm:min-w-[72px] py-2 border-b-2 transition-all ${
                activeCategory === ''
                  ? isGoodWork
                    ? 'border-indigo-700 text-indigo-700'
                    : 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="w-7 h-7 sm:w-6 sm:h-6 flex items-center justify-center">
                <Globe className="w-6 h-6 sm:w-5 sm:h-5" />
              </div>
              <span className="text-xs font-medium whitespace-nowrap">Alle</span>
            </button>

            {/* Kategorien (dynamisch je nach Realm) */}
            {categories.map((cat) => {
              const IconComponent = ICON_MAP[cat.icon];
              const isActive = activeCategory === cat.id;

              return (
                <button
                  key={cat.id}
                  onClick={() => onCategoryChange(isActive ? '' : cat.id as MainCategory | GoodWorkCategory)}
                  className={`flex flex-col items-center gap-1.5 min-w-[48px] sm:min-w-[72px] py-2 border-b-2 transition-all ${
                    isActive
                      ? isGoodWork
                        ? 'border-indigo-700 text-indigo-700'
                        : 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  title={cat.description}
                >
                  <div className="w-7 h-7 sm:w-6 sm:h-6 flex items-center justify-center">
                    {IconComponent && <IconComponent className="w-6 h-6 sm:w-5 sm:h-5" />}
                  </div>
                  <span className="text-xs font-medium whitespace-nowrap">
                    <span className="sm:hidden">{shortLabels[cat.id]}</span>
                    <span className="hidden sm:inline">{cat.label}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Right scroll button */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-md hover:shadow-lg transition-shadow"
              aria-label="Nach rechts scrollen"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CategoryBar;
